import { useEffect, useReducer, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { TextField, Checkbox } from '../../components/input';
import { HugeiconsIcon } from '@hugeicons/react';
import { ViewIconStroke, ViewOffSlashIconStroke } from '@/components/icons';

import { T } from '../../util/Localize';
import Api from '../../util/Api';
import { isValidEmail } from '../../util/Functions';
import { useSession } from '../../contexts/session-context';
import { HUB_FORGOT_PASSWORD_PATH, HUB_REGISTER_PATH } from '../../routes/paths';
import { SuspendedAccountNotice } from '../../components/errors/suspended-account-notice';
import { postLoginDestination } from './post-login-destination';
import {
    WELCOME_HEADING_DEFAULT,
    WELCOME_BODY_DEFAULT,
    SIGN_IN_LABEL_DEFAULT,
    LOGIN_WELCOME_HEADING_VAR,
    LOGIN_WELCOME_BODY_VAR,
    LOGIN_SIGN_IN_LABEL_VAR,
    resolveCssText
} from '../business/brand-api/login-branding-defaults';
import { getThemeValue, subscribeThemeChange } from '../../util/Theme';
import { useHost } from '../../hooks/use-host';
import { canRegisterOnBrand } from '../../services/registration';
import { AuthPageLayout } from './auth-page-layout';
import { authSubmitButtonSx } from './auth-submit-button';
import { GoogleSignInButton } from './google-sign-in-button';
import type { SsoOutcome } from '../../services/google-sso';

/** Steps the welcome heading down at longer lengths so the 50-char max still wraps to no
 * more than two lines at mobile width (UX-795), rather than a single fixed size. */
export function headingFontSize(heading: string): number {
    if (heading.length <= 20) return 36;
    if (heading.length <= 35) return 30;
    // At 24px only ~26 characters fit a 375px-wide column, so a 50-character heading took three
    // lines; 20px fits ~31 per line, leaving room for word-wrap slack.
    if (heading.length <= 42) return 22;
    return 20;
}

export interface LoginProps {
    /**
     * UX-802: the Google return outcome, when this page load is one. Only `hub-login-page.tsx`
     * passes it. `failed` shows the Google error; so does `success` (and `created`, treated like
     * it), because a session outcome reaching the form means the returned session did not
     * authorize. `email-in-use` is a sign-up outcome that should not reach this page; it is treated
     * like `failed` (UX-803). `no-account` never renders the form. `suspended` (ROBO-481) shows the
     * Account Inactive notice instead, as a 403 `BRAND_SUSPENDED` from the password form does.
     */
    ssoOutcome?: SsoOutcome;
    /**
     * UX-833 AC4: the studio URL this form is being shown on, when the studio shell renders it in
     * place of the app (`layout-component.tsx` — a deep link opened signed out, or a session that
     * expired mid-work). Sign-in returns there instead of the studio home, so the user picks up
     * where they were. The hub login page passes nothing: `/ux/login` is not somewhere to return
     * to, and Sign out deliberately lands there (AC1).
     */
    returnTo?: string;
    /**
     * Called when the form's own sign-in succeeds, just before the redirect. The hub login page uses
     * it to keep this form (and its spinner button) on screen until the page navigates, rather than
     * swapping it for its full-page spinner the moment the session becomes authenticated.
     */
    onSignedIn?: () => void;
    /**
     * Stay on this page after signing in instead of navigating anywhere: the host page re-renders
     * from the session, which is now authenticated. For a page that shows this form inline and is
     * itself the destination — the MCP consent screen, whose connection request lives in its own
     * query string (`hub-mcp-connect-page.tsx`).
     *
     * ★ NOT `returnTo`. `returnTo` only honours studio paths (`postLoginDestination`), and it means
     * "this form is inside the studio shell" to the hub links below. Widening it would have changed
     * both; this changes neither. The suspended-account and client-user landing rules are also
     * skipped — on purpose: a page that asks for this handles who may use it itself, as the consent
     * screen does with its own not-permitted state.
     */
    stayOnPage?: boolean;
    /**
     * ROBO-961: leave out every way to create an account — the "New client? Create an account" link.
     * For a page that only lets an existing account in (the delivery page's Contact Info tab). The
     * Google button stays: it signs in, it does not sign up.
     */
    hideSignUp?: boolean;
    /**
     * ROBO-961: `'inline'` draws the form alone, inside the host's own panel (a drawer tab), with the
     * theme's colours; `'page'` (default) draws it in the login page's frame (`AuthPageLayout`): header,
     * background, the dark panel and the legal links. Inline leaves out the brand's welcome heading too.
     */
    layout?: 'page' | 'inline';
}

export default function Login({
    ssoOutcome,
    returnTo,
    onSignedIn,
    stayOnPage = false,
    hideSignUp = false,
    layout = 'page'
}: LoginProps = {}) {
    const isInline = layout === 'inline';
    const { login, refusal } = useSession();
    const { data: hostInfo } = useHost();
    // ROBO-478: the API refused a session because the business is suspended — at this form's
    // sign-in, on the boot authorize, or mid-session. It replaces both error messages below: the
    // password was right, and a Google return that was refused did not fail for a Google reason.
    // ROBO-481: a Google return refused for suspension carries no session to refuse, so it arrives
    // as `sso=suspended` instead; the notice lasts until the user tries to sign in again.
    const [isGoogleSuspended, setIsGoogleSuspended] = useState(ssoOutcome === 'suspended');
    const isBrandSuspended = refusal === 'brand-suspended' || isGoogleSuspended;
    // Never prefill from storage. These fields used to be persisted to localStorage on every
    // keystroke — including the password, in plaintext — as an early-development convenience
    // (ROBO-275). Removed: any script on the origin could read a valid user password, and
    // localStorage has no expiry. See purgeLegacyCredentialStorage() for the cleanup of
    // values already sitting in existing users' browsers.
    const [credentials, setCredentials] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [authorizing, setAuthorizing] = useState(false);
    const [failed, setFailed] = useState(false);
    const [isGoogleSignInFailed, setIsGoogleSignInFailed] = useState(
        ssoOutcome !== undefined && ssoOutcome !== 'no-account' && ssoOutcome !== 'suspended'
    );
    const [showServer, setShowServer] = useState<boolean>(false);
    const [apiServer, setApiServer] = useState<string>(Api.apiHost);

    /**
     * Only the STUDIO shell (`layout-component.tsx`) passes `returnTo`. Its router has no hub
     * routes, so the hub links below must be full page loads there, letting the host serve the hub
     * bundle. On the hub's own pages they are ordinary client-side (SPA) navigations. The inline form
     * is hosted outside the hub too (ROBO-961: the delivery shell), so its links are full loads as well.
     */
    const inStudioShell = returnTo !== undefined || isInline;

    const emailValid = isValidEmail(credentials.email);
    const canSubmit = emailValid && credentials.password.length > 0;

    // UX-800: these three live in the brand's theme CSS custom properties, not on `hostInfo`
    // (API-549 permanently descoped them as brand columns) — read straight from Theme.ts's cache.
    // That cache is populated asynchronously by `HostEnvironment` once the public host fetch
    // lands, and reading it here doesn't itself trigger a re-render, so this subscribes to
    // `subscribeThemeChange` to pick up the real values once they arrive instead of getting stuck
    // on the defaults for the rest of the session.
    const [, forceThemeRerender] = useReducer((tick: number) => tick + 1, 0);
    useEffect(() => subscribeThemeChange(forceThemeRerender), []);

    const heading = resolveCssText(
        getThemeValue('BODY', LOGIN_WELCOME_HEADING_VAR),
        WELCOME_HEADING_DEFAULT
    );
    const body = resolveCssText(
        getThemeValue('BODY', LOGIN_WELCOME_BODY_VAR),
        WELCOME_BODY_DEFAULT
    );
    const signInLabel =
        resolveCssText(getThemeValue('BODY', LOGIN_SIGN_IN_LABEL_VAR), SIGN_IN_LABEL_DEFAULT) ||
        SIGN_IN_LABEL_DEFAULT;

    useEffect(() => {
        let keyCount: number = 0;
        const keyCheck = (e: any) => {
            if (e.key === '`') {
                keyCount++;
                if (keyCount > 2) {
                    setShowServer(true);
                }
                return false;
            } else {
                keyCount = 0;
            }
        };
        window.addEventListener('keypress', keyCheck);
        return () => {
            window.removeEventListener('keypress', keyCheck);
        };
    }, []);

    const handleLogin = async () => {
        // Enter in a field submits the form even with the button disabled.
        if (authorizing || !canSubmit) return;
        setAuthorizing(true);
        setFailed(false);
        setIsGoogleSignInFailed(false);
        setIsGoogleSuspended(false);
        let signedIn = false;
        try {
            const ok = await login(credentials.email, credentials.password, rememberMe);
            if (ok) {
                signedIn = true;
                onSignedIn?.();
                if (stayOnPage) return;
                // `Api.user`, not `useSession().user`: this closure predates the session update,
                // and `Api.sessionUpdate` has already set the user by the time `login` resolves.
                // The hub login page makes the same decision from context once it re-renders.
                window.location.assign(postLoginDestination(Api.user, returnTo));
            } else {
                setFailed(true);
            }
        } finally {
            // On success the page is navigating away: keep the button in its loading state until
            // it does, instead of flashing back to "Sign In".
            if (!signedIn) setAuthorizing(false);
        }
    };

    // On the dark login panel these are light; inline they sit on the theme's own background.
    const errorColor = isInline ? 'error.main' : '#ff8a80';
    const form = (
        <Box display="flex" flexDirection="column" gap={4} data-testid="login-form">
            {!isInline && (heading || body) && (
                <Box>
                    {heading && (
                        <Typography
                            sx={{
                                color: 'inherit',
                                fontWeight: 900,
                                fontSize: headingFontSize(heading),
                                lineHeight: 1
                            }}
                        >
                            {heading}
                        </Typography>
                    )}
                    {body && (
                        <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1 }}>
                            {body}
                        </Typography>
                    )}
                </Box>
            )}
            <Box
                component="form"
                display="flex"
                flexDirection="column"
                gap={1.25}
                onSubmit={(e: React.FormEvent) => {
                    e.preventDefault();
                    handleLogin();
                }}
            >
                <TextField
                    type="email"
                    label={T('Email Address')}
                    value={credentials.email}
                    disabled={authorizing}
                    error={credentials.email.length > 0 && !emailValid}
                    onChange={(e: any) => {
                        setCredentials(prev => ({ ...prev, email: e.target.value }));
                    }}
                    labelSx={{ color: 'inherit' }}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="baseline">
                        <Typography sx={{ color: 'inherit', fontSize: 13 }}>
                            {T('Password')}
                        </Typography>
                        <RouterLink
                            to={HUB_FORGOT_PASSWORD_PATH}
                            reloadDocument={inStudioShell}
                            style={{ color: 'inherit', fontSize: 12 }}
                        >
                            {T('Forgot password?')}
                        </RouterLink>
                    </Box>
                    <TextField
                        type={showPassword ? 'text' : 'password'}
                        aria-label={T('Password')}
                        value={credentials.password}
                        disabled={authorizing}
                        onChange={(e: any) => {
                            setCredentials(prev => ({ ...prev, password: e.target.value }));
                        }}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? (
                                                <HugeiconsIcon
                                                    icon={ViewOffSlashIconStroke}
                                                    strokeWidth={1.5}
                                                />
                                            ) : (
                                                <HugeiconsIcon
                                                    icon={ViewIconStroke}
                                                    strokeWidth={1.5}
                                                />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                </Box>
                {showServer && (
                    <TextField
                        label="Server"
                        value={apiServer}
                        disabled={authorizing}
                        onChange={(e: any) => setApiServer(e.target.value)}
                        onBlur={(e: any) => {
                            const host = (Api.apiHost = e.target.value);
                            localStorage.setItem('apihost-override', host);
                        }}
                        labelSx={{ color: 'inherit' }}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                )}
                {isBrandSuspended && <SuspendedAccountNotice sx={{ mt: 0.5 }} />}
                {failed && !isBrandSuspended && (
                    <Typography sx={{ color: errorColor, fontSize: 13 }}>
                        {T('The email address or password you entered is incorrect.')}
                    </Typography>
                )}
                {isGoogleSignInFailed && !isBrandSuspended && (
                    <Typography sx={{ color: errorColor, fontSize: 13 }}>
                        {T("We couldn't sign you in with Google. Please try again.")}
                    </Typography>
                )}
                <Checkbox
                    checked={rememberMe}
                    onChange={(_, checked) => setRememberMe(checked)}
                    label={T('Keep me signed in on this device')}
                    formControlLabelSx={{
                        mt: 0.5,
                        '& .MuiFormControlLabel-label': { color: 'inherit' }
                    }}
                />
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={!canSubmit || authorizing}
                    sx={isInline ? { mt: 1, py: 1 } : authSubmitButtonSx}
                >
                    {authorizing ? (
                        <CircularProgress size={22} sx={{ color: 'inherit' }} />
                    ) : (
                        signInLabel
                    )}
                </Button>

                <Divider
                    sx={{
                        my: 0.5,
                        color: 'inherit',
                        fontSize: 13,
                        '&::before, &::after': {
                            borderTop: '1px solid currentColor',
                            opacity: 0.4
                        }
                    }}
                >
                    {T('or')}
                </Divider>
                <GoogleSignInButton
                    disabled={authorizing}
                    // `stayOnPage` covers the password form; Google leaves the page, so the
                    // way back is remembered for /ux/login to follow (google-sso.ts).
                    resumeTo={
                        stayOnPage
                            ? `${window.location.pathname}${window.location.search}`
                            : undefined
                    }
                    onRedirectStart={() => {
                        setFailed(false);
                        setIsGoogleSignInFailed(false);
                        setIsGoogleSuspended(false);
                    }}
                    onRedirectError={() => setIsGoogleSignInFailed(true)}
                />
                {/* UX-797 / API-507: only on a brand that can take a client registration
                 * (brand 2 or greater). The Google button above is NOT gated — signing IN is
                 * available on every brand; only the route to registration is conditional.
                 * ROBO-961: and never with `hideSignUp`. */}
                {!hideSignUp && canRegisterOnBrand(hostInfo?.brandId) && (
                    <Box display="flex" gap={0.75} justifyContent="center" sx={{ fontSize: 14 }}>
                        <Typography sx={{ color: 'inherit', fontSize: 'inherit' }}>
                            {T('New client?')}
                        </Typography>
                        <RouterLink
                            to={HUB_REGISTER_PATH}
                            reloadDocument={inStudioShell}
                            style={{ color: 'inherit', fontWeight: 700 }}
                        >
                            {T('Create an account')}
                        </RouterLink>
                    </Box>
                )}
            </Box>
        </Box>
    );

    return isInline ? form : <AuthPageLayout>{form}</AuthPageLayout>;
}
